import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Loader2, Mail, Phone, MapPin, MessageCircle, Send, ArrowRight,
  Sparkles, Compass, HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import BookingPage from "@/pages/BookingPage";
import BookingPageLegacy from "@/pages/BookingPageLegacy";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import GeneralInquiryForm from "@/components/GeneralInquiryForm";
import SyncedPropertyCarousel from "@/components/SyncedPropertyCarousel";
import PropertyCardMiniMap from "@/components/PropertyCardMiniMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// All properties share this single reservations address for now. The
// `propertyContacts` map below lets us override per-slug as the team
// adds direct lines (phone, WhatsApp) — leave entries empty otherwise.
const SHARED_RESERVATIONS_EMAIL = "xaluca@xaluca.com";
const propertyContacts = {
  // Example future override:
  // "kasbah-hotel-xaluca-arfoud": { phone: "+212 535 57 84 50", whatsapp: "+212 6XX XX XX XX" },
};

// Maps URL ?topic= slug → pre-filled subject for the General Inquiry form.
const TOPIC_SUBJECTS = {
  "spa-arfoud": { en: "Spa enquiry — Kasbah Hotel Xaluca Arfoud", fr: "Demande spa — Kasbah Hotel Xaluca Arfoud", es: "Consulta spa — Kasbah Hotel Xaluca Arfoud" },
  "spa-tombouctou": { en: "Spa enquiry — Kasbah Hotel Tombouctou", fr: "Demande spa — Kasbah Hotel Tombouctou", es: "Consulta spa — Kasbah Hotel Tombouctou" },
  "spa-riad": { en: "Spa enquiry — Hotel Riad Xaluca", fr: "Demande spa — Hotel Riad Xaluca", es: "Consulta spa — Hotel Riad Xaluca" },
  "spa-dades": { en: "Spa enquiry — Hotel Xaluca Dades", fr: "Demande spa — Hotel Xaluca Dades", es: "Consulta spa — Hotel Xaluca Dades" },
  "spa-sidi-ali": { en: "Spa enquiry — Hotel Xaluca Spa Aguelmame Sidi Ali", fr: "Demande spa — Hotel Xaluca Spa Aguelmame Sidi Ali", es: "Consulta spa — Hotel Xaluca Spa Aguelmame Sidi Ali" },
};

const RITUAL_MESSAGE = {
  en: (ritual) => `Hello,\n\nI would like to enquire about the ritual "${ritual}". Could you please share availability, duration and any preparation advice?\n\nMany thanks,`,
  fr: (ritual) => `Bonjour,\n\nJe souhaite me renseigner sur le rituel « ${ritual} ». Pourriez-vous me communiquer les disponibilités, la durée et les éventuels conseils de préparation ?\n\nMerci par avance,`,
  es: (ritual) => `Hola,\n\nMe gustaría informarme sobre el ritual «${ritual}». ¿Podrían indicarme disponibilidad, duración y cualquier consejo de preparación?\n\nMuchas gracias,`,
};

const COPY = {
  hero_overline: { en: "Contact · Reservations", fr: "Contact · Réservations", es: "Contacto · Reservas" },
  hero_title: { en: "How may we host you?", fr: "Comment vous accueillir ?", es: "¿Cómo podemos atenderle?" },
  hero_sub: {
    en: "Three doors, one family. Open a detailed travel request, send a quick booking, or ask a general question — every channel reaches the same Catalan-Berber team in Erfoud.",
    fr: "Trois portes, une famille. Ouvrez une demande de voyage détaillée, envoyez une réservation rapide ou posez une question générale — chaque canal arrive à la même équipe catalano-berbère à Erfoud.",
    es: "Tres puertas, una familia. Abra una solicitud detallada, envíe una reserva rápida o haga una consulta general — todos los canales llegan al mismo equipo catalano-bereber en Erfoud.",
  },

  tabs_intro_overline: { en: "Choose how you'd like to reach us", fr: "Choisissez votre canal", es: "Elija su canal" },
  detailed_label: { en: "Detailed Booking", fr: "Demande détaillée", es: "Reserva detallada" },
  quick_label: { en: "Quick Booking", fr: "Demande rapide", es: "Reserva rápida" },
  inquiry_label: { en: "General Inquiry", fr: "Demande générale", es: "Consulta general" },

  detailed_desc: {
    en: "Request a tailored, multi-property travel proposal — hotels, bivouacs, experiences, transport. Best when you're building a full itinerary.",
    fr: "Demandez une proposition de voyage sur-mesure — hôtels, bivouacs, expériences, transport. Idéal pour un itinéraire complet.",
    es: "Solicite una propuesta de viaje a medida — hoteles, vivacs, experiencias, transporte. Ideal para un itinerario completo.",
  },
  quick_desc: {
    en: "A short form for simple requests — you already know the property and the dates.",
    fr: "Un formulaire court pour les demandes simples — vous connaissez déjà l'adresse et les dates.",
    es: "Un formulario breve para peticiones sencillas — ya conoce el alojamiento y las fechas.",
  },
  inquiry_desc: {
    en: "Anything else — general questions, press, collaborations, corporate enquiries.",
    fr: "Tout le reste — questions générales, presse, collaborations, demandes corporate.",
    es: "Lo demás — consultas generales, prensa, colaboraciones, contacto corporativo.",
  },

  props_overline: { en: "Our houses · Direct contact", fr: "Nos maisons · Contact direct", es: "Nuestras casas · Contacto directo" },
  props_title: { en: "Speak to a specific property", fr: "Contacter une adresse précise", es: "Hablar con un alojamiento concreto" },
  props_lead: {
    en: "Every property below shares the central reservations inbox for now — direct lines and WhatsApp numbers will appear here as each house publishes them.",
    fr: "Chaque adresse partage la boîte de réservations centrale pour l'instant — les lignes directes et numéros WhatsApp apparaîtront ici dès leur publication par chaque maison.",
    es: "Cada alojamiento comparte la bandeja de reservas central por ahora — las líneas directas y números de WhatsApp aparecerán aquí en cuanto cada casa los publique.",
  },
  contact_via_email: { en: "Email reservations", fr: "Écrire aux réservations", es: "Escribir a reservas" },
  open_property: { en: "Open property page", fr: "Voir la page", es: "Ver la página" },
  coming_soon: { en: "Direct line coming soon", fr: "Ligne directe bientôt disponible", es: "Línea directa próximamente" },

  inquiry_name: { en: "Your name", fr: "Votre nom", es: "Su nombre" },
  inquiry_email: { en: "Your email", fr: "Votre e-mail", es: "Su correo" },
  inquiry_subject: { en: "Subject", fr: "Sujet", es: "Asunto" },
  inquiry_message: { en: "Your message", fr: "Votre message", es: "Su mensaje" },
  inquiry_send: { en: "Send message", fr: "Envoyer", es: "Enviar mensaje" },
  inquiry_success: { en: "Thank you. We'll be in touch within 24 hours.", fr: "Merci. Nous revenons vers vous sous 24 heures.", es: "Gracias. Le respondemos en 24 horas." },
  inquiry_error: { en: "Sorry — something went wrong. Try again or email us directly.", fr: "Désolé — une erreur est survenue. Réessayez ou écrivez-nous directement.", es: "Lo sentimos — algo ha fallado. Reintente o escríbanos directamente." },
};

const TAB_DEFS = [
  { id: "detailed", icon: Sparkles, label: COPY.detailed_label, desc: COPY.detailed_desc },
  { id: "quick", icon: Compass, label: COPY.quick_label, desc: COPY.quick_desc },
  { id: "inquiry", icon: HelpCircle, label: COPY.inquiry_label, desc: COPY.inquiry_desc },
];

// ────────────────────────────────────────────────────────────────────────
// Property contact grid — thumbnail · name · address · email · slots for
// future direct lines (phone / WhatsApp / property-specific form).
// ────────────────────────────────────────────────────────────────────────
const PropertyContactGrid = ({ lang }) => (
  <section
    className="py-20 md:py-28 bg-[#FDFBF7]"
    data-testid="contact-properties"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-3xl mb-12">
        <p className="overline text-[#C16542]">{pick(COPY.props_overline, lang)}</p>
        <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
          {pick(COPY.props_title, lang)}
        </h2>
        <p className="mt-5 text-base text-[#5C5248] leading-relaxed">
          {pick(COPY.props_lead, lang)}
        </p>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8" data-testid="contact-properties-grid">
        {HOTELS.map((h) => {
          const overrides = propertyContacts[h.slug] || {};
          const phone = overrides.phone;
          const whatsapp = overrides.whatsapp;
          return (
            <li
              key={h.slug}
              className="bg-white border border-[#2C2621]/8 flex flex-col"
              data-testid={`contact-property-${h.slug}`}
              data-cms-scope={`contact-property.${h.slug}`}
            >
              {/* Thumbnail — 8-image carousel powered by the shared
                  propertyGalleries source. Consumes CMS overrides from
                  /admin/galleries → "Hotel card images" so /contact and
                  /booking always render identical imagery for the same
                  slug. */}
              <SyncedPropertyCarousel
                slug={h.slug}
                alt={h.name}
                href={`/hotels/${h.slug}`}
                testIdPrefix={`contact-property-thumb-${h.slug}`}
                aspectRatio="4/3"
                overlays={
                  <span
                    className={`absolute top-3 left-3 inline-block text-[9px] tracking-[0.28em] uppercase px-2 py-1 rounded-full backdrop-blur-sm ${
                      h.category === "bivouac"
                        ? "bg-[#D4A373]/85 text-[#1A1513]"
                        : h.category === "refuge"
                        ? "bg-[#5A6B4F]/85 text-[#FDFBF7]"
                        : "bg-[#FDFBF7]/85 text-[#2C2621]"
                    }`}
                  >
                    {h.category === "bivouac"
                      ? pick({ en: "Bivouac", fr: "Bivouac", es: "Vivac" }, lang)
                      : h.category === "refuge"
                      ? pick({ en: "Mountain refuge", fr: "Refuge", es: "Refugio" }, lang)
                      : pick({ en: "Hotel", fr: "Hôtel", es: "Hotel" }, lang)}
                  </span>
                }
              />

              {/* Body */}
              <div className="flex-1 flex flex-col p-5">
                <h3 className="font-serif-x text-xl leading-tight" data-testid={`contact-property-name-${h.slug}`}>
                  {h.name}
                </h3>
                <p className="mt-1.5 inline-flex items-start gap-1.5 text-xs text-[#5C5248] leading-relaxed" data-testid={`contact-property-address-${h.slug}`}>
                  <MapPin className="w-3 h-3 mt-0.5 text-[#A07042] shrink-0" strokeWidth={1.6} />
                  <span>
                    {h.location}
                    {h.region && <span className="block text-[#5C5248]/70">{pick({ en: h.region, fr: h.region_fr || h.region, es: h.region_es || h.region }, lang)}</span>}
                  </span>
                </p>

                {/* Full intro description — never truncated, always
                    visible. Card height adapts to content length. */}
                {h.intro ? (
                  <p
                    className="mt-3 text-[13px] leading-relaxed text-[#2C2621]/85 font-light"
                    data-testid={`contact-property-intro-${h.slug}`}
                  >
                    {pick(h.intro, lang)}
                  </p>
                ) : null}

                {/* Compact satellite mini-map — same tile provider + pin
                    as the "About the hotel" section on the individual
                    property page. Lazy-inited to avoid spawning 14
                    Leaflet instances until the card scrolls into view. */}
                <PropertyCardMiniMap
                  hotelSlug={h.slug}
                  hotelName={h.name}
                  hotelLocation={h.location}
                  hotelCategory={h.category}
                  aspectRatio="16/9"
                  className="mt-4"
                />

                {/* Contact channels */}
                <div className="mt-4 pt-4 border-t border-[#2C2621]/10 space-y-2">
                  <a
                    href={`mailto:${SHARED_RESERVATIONS_EMAIL}?subject=${encodeURIComponent(`Enquiry — ${h.name}`)}`}
                    className="flex items-start gap-2 text-xs text-[#2C2621] hover:text-[#C16542] transition-colors"
                    data-testid={`contact-property-email-${h.slug}`}
                  >
                    <Mail className="w-3.5 h-3.5 mt-0.5 text-[#C16542] shrink-0" strokeWidth={1.6} />
                    <span className="break-all">{SHARED_RESERVATIONS_EMAIL}</span>
                  </a>

                  {/* Future phone (slot — rendered only when set) */}
                  {phone && (
                    <a
                      href={`tel:${phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-2 text-xs text-[#2C2621] hover:text-[#C16542] transition-colors"
                      data-testid={`contact-property-phone-${h.slug}`}
                    >
                      <Phone className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
                      <span>{phone}</span>
                    </a>
                  )}

                  {/* Future WhatsApp (slot — rendered only when set) */}
                  {whatsapp && (
                    <a
                      href={`https://wa.me/${whatsapp.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-[#2C2621] hover:text-[#C16542] transition-colors"
                      data-testid={`contact-property-whatsapp-${h.slug}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
                      <span>{whatsapp}</span>
                    </a>
                  )}

                  {/* When no direct lines are set, hint at the roadmap */}
                  {!phone && !whatsapp && (
                    <p
                      className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/60"
                      data-testid={`contact-property-soon-${h.slug}`}
                    >
                      {pick(COPY.coming_soon, lang)}
                    </p>
                  )}
                </div>

                <a
                  href={`/hotels/${h.slug}`}
                  className="mt-auto pt-5 inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-[#C16542] hover:text-[#A35133] self-start transition-colors"
                  data-testid={`contact-property-page-${h.slug}`}
                >
                  <span className="border-b border-[#C16542]/40 hover:border-[#A35133] pb-1">
                    {pick(COPY.open_property, lang)}
                  </span>
                  <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
                </a>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  </section>
);


// ────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────
const ContactPage = () => {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("detailed");
  const tabsRef = useRef(null);

  // Pre-fill General Inquiry from deep links (e.g. ?topic=spa-arfoud&ritual=X)
  const { prefillSubject, prefillMessage } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const topic = params.get("topic");
    const ritual = params.get("ritual");
    if (!topic && !ritual) return { prefillSubject: "", prefillMessage: "" };
    const topicLine = topic && TOPIC_SUBJECTS[topic]
      ? TOPIC_SUBJECTS[topic][lang] || TOPIC_SUBJECTS[topic].en
      : "";
    const subject = ritual && topicLine ? `${topicLine} · ${ritual}` : topicLine || (ritual ? `Spa ritual enquiry — ${ritual}` : "");
    const message = ritual && RITUAL_MESSAGE[lang] ? RITUAL_MESSAGE[lang](ritual) : "";
    return { prefillSubject: subject, prefillMessage: message };
  }, [location.search, lang]);

  // If a deep link supplies a subject/message we switch to the inquiry tab.
  useEffect(() => {
    if (prefillSubject || prefillMessage) {
      setActiveTab("inquiry");
    }
  }, [prefillSubject, prefillMessage]);

  // Allow ?tab=detailed|quick|inquiry to drive the initial tab.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab && TAB_DEFS.some((t) => t.id === tab)) setActiveTab(tab);
  }, [location.search]);

  // ?hotel=<slug> or ?hotels=<slug,…> (sent by every "Contact Us" CTA on
  // property cards) → force the Detailed Booking tab. The embedded
  // <BookingPage /> already reads the same params and pre-selects the
  // property in the shared BookingProvider, so the visitor lands with
  // the form open AND their chosen hotel ticked — same one-click flow
  // as the "Book Now" CTA, just routed through Contact.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasHotelIntent =
      params.get("hotel") || params.get("hotels") || params.get("items");
    if (!hasHotelIntent) return;
    setActiveTab("detailed");
    // Defer scroll until the panel has mounted on next paint.
    window.requestAnimationFrame(() => {
      const el = document.getElementById("tabpanel-detailed");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.search]);

  /**
   * One-click handler for the hero CTAs and the tab switcher cards.
   * Activates the chosen channel and smooth-scrolls to its form panel so
   * the visitor never has to scroll or pick a tab a second time.
   */
  const selectChannel = useCallback((id) => {
    setActiveTab(id);
    // Defer to next frame so the target panel has rendered before we scroll.
    window.requestAnimationFrame(() => {
      const el = document.getElementById(`tabpanel-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  return (
    <div data-testid="page-contact">

      {/* HERO — multi-slide (video first, then concierge photography).
          The three contact-channel CTAs sit INSIDE the hero narrative,
          directly beneath the description, via PageHero's `extraCtas`
          slot. This keeps the channel picker as a first-class part of
          the hero content rather than a floating overlay. */}
      <section data-testid="contact-hero">
        <PageHero
          images={[
            "https://images.unsplash.com/photo-1542401886-65d6c61db217?auto=format&fit=crop&w=2400&q=80",
            "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=2400&q=80",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
          ]}
          alt="Contact · Grup Xaluca"
          scope="page.contact"
          overline={pick(COPY.hero_overline, lang)}
          title={pick(COPY.hero_title, lang)}
          sub={pick(COPY.hero_sub, lang)}
          extraCtas={
            <div
              className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4"
              data-testid="contact-hero-cta-overlay"
            >
              {TAB_DEFS.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={`hero-cta-${tab.id}`}
                    type="button"
                    onClick={() => selectChannel(tab.id)}
                    aria-pressed={active}
                    aria-controls={`tabpanel-${tab.id}`}
                    className={`group text-left p-4 md:p-5 backdrop-blur-md transition-all duration-300 border-2 flex items-start gap-3 md:gap-4 ${
                      active
                        ? "bg-[#C16542]/95 text-[#FDFBF7] border-[#D4A373] shadow-[0_12px_40px_-12px_rgba(193,101,66,0.65)] -translate-y-1"
                        : "bg-[#1A1513]/55 text-[#FDFBF7] border-[#FDFBF7]/25 hover:bg-[#1A1513]/75 hover:border-[#D4A373] hover:-translate-y-0.5"
                    }`}
                    data-testid={`contact-hero-cta-${tab.id}`}
                  >
                    <span
                      className={`shrink-0 inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full transition-colors ${
                        active ? "bg-[#FDFBF7]/15" : "bg-[#FDFBF7]/10 group-hover:bg-[#D4A373]/20"
                      }`}
                    >
                      <Icon className={`w-4 h-4 md:w-5 md:h-5 ${active ? "text-[#FDFBF7]" : "text-[#D4A373]"}`} strokeWidth={1.6} />
                    </span>
                    <span className="flex flex-col min-w-0 flex-1">
                      <span className="font-serif-x text-base md:text-lg leading-tight">
                        {pick(tab.label, lang)}
                      </span>
                      <span className={`text-[10px] md:text-[11px] tracking-[0.18em] uppercase mt-1.5 ${active ? "text-[#FDFBF7]/85" : "text-[#FDFBF7]/65"}`}>
                        {active
                          ? pick({ en: "Selected · scroll for form", fr: "Sélectionné · descendez", es: "Seleccionado · descienda" }, lang)
                          : pick({ en: "Open this form", fr: "Ouvrir ce formulaire", es: "Abrir formulario" }, lang)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          }
        />
      </section>

      {/* TAB SWITCHER */}
      <section
        ref={tabsRef}
        className="py-16 md:py-20 border-b border-[#2C2621]/10 bg-[#FBF6EE]/60"
        data-testid="contact-tabs"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline text-[#C16542]">{pick(COPY.tabs_intro_overline, lang)}</p>
          <div
            role="tablist"
            aria-label="Contact channels"
            className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4"
          >
            {TAB_DEFS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={`tabpanel-${tab.id}`}
                  id={`tab-${tab.id}`}
                  onClick={() => selectChannel(tab.id)}
                  className={`text-left p-5 md:p-6 transition-colors flex flex-col h-full ${
                    active
                      ? "bg-[#2C2621] text-[#FDFBF7] border-2 border-[#2C2621]"
                      : "bg-white border-2 border-[#2C2621]/10 hover:border-[#C16542]/50"
                  }`}
                  data-testid={`contact-tab-${tab.id}`}
                >
                  <Icon
                    className={`w-5 h-5 mb-3 ${active ? "text-[#D4A373]" : "text-[#C16542]"}`}
                    strokeWidth={1.5}
                  />
                  <h3 className={`font-serif-x text-xl md:text-2xl tracking-tight ${active ? "text-[#FDFBF7]" : "text-[#2C2621]"}`}>
                    {pick(tab.label, lang)}
                  </h3>
                  <p className={`mt-2 text-sm leading-relaxed flex-1 ${active ? "text-[#FDFBF7]/80" : "text-[#5C5248]"}`}>
                    {pick(tab.desc, lang)}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* TAB PANELS */}
      <div
        id="tabpanel-detailed"
        role="tabpanel"
        aria-labelledby="tab-detailed"
        hidden={activeTab !== "detailed"}
        data-testid="contact-panel-detailed"
      >
        {activeTab === "detailed" && <BookingPage embedded />}
      </div>

      <div
        id="tabpanel-quick"
        role="tabpanel"
        aria-labelledby="tab-quick"
        hidden={activeTab !== "quick"}
        data-testid="contact-panel-quick"
      >
        {activeTab === "quick" && <BookingPageLegacy embedded />}
      </div>

      <div
        id="tabpanel-inquiry"
        role="tabpanel"
        aria-labelledby="tab-inquiry"
        hidden={activeTab !== "inquiry"}
        data-testid="contact-panel-inquiry"
      >
        {activeTab === "inquiry" && (
          <section className="py-20 md:py-28 bg-[#FDFBF7]">
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
              <div className="md:col-span-5">
                <p className="overline text-[#C16542]">{pick(COPY.inquiry_label, lang)}</p>
                <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
                  {pick(
                    {
                      en: "Anything else — we read every message.",
                      fr: "Tout le reste — chaque message est lu.",
                      es: "Lo demás — leemos cada mensaje.",
                    },
                    lang
                  )}
                </h2>
                <p className="mt-5 text-base text-[#5C5248] leading-relaxed">
                  {pick(COPY.inquiry_desc, lang)}
                </p>
                <div className="mt-8 space-y-3">
                  <a
                    href={`mailto:${SHARED_RESERVATIONS_EMAIL}`}
                    className="flex items-center gap-2.5 text-sm text-[#2C2621] hover:text-[#C16542] transition-colors"
                  >
                    <Mail className="w-4 h-4 text-[#C16542]" strokeWidth={1.6} />
                    {SHARED_RESERVATIONS_EMAIL}
                  </a>
                  <a
                    href="tel:+212535578450"
                    className="flex items-center gap-2.5 text-sm text-[#2C2621] hover:text-[#C16542] transition-colors"
                  >
                    <Phone className="w-4 h-4 text-[#C16542]" strokeWidth={1.6} />
                    +212 535 57 84 50
                  </a>
                  <p className="flex items-center gap-2.5 text-sm text-[#5C5248]">
                    <MapPin className="w-4 h-4 text-[#C16542]" strokeWidth={1.6} />
                    Erfoud · southern Morocco
                  </p>
                </div>
              </div>
              <div className="md:col-span-7">
                <GeneralInquiryForm
                  prefillSubject={prefillSubject}
                  prefillMessage={prefillMessage}
                />
              </div>
            </div>
          </section>
        )}
      </div>

      {/* PROPERTIES GRID */}
      <PropertyContactGrid lang={lang} />
    </div>
  );
};

export default ContactPage;
